import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { PageSection } from '@ui/components/PageSection'
import { CirclePlusIcon } from '@ui/icons/CirclePlus.icon'

import { useAccountActionAccessCheck } from '@utils/AccountAccess/accountAccessCheck'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { NotificationWarning } from '@global/notification/notification'

import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'

import { BankAccountsCard } from './BankAccountsCard'
import { BankAccountsTable } from './BankAccountsTable'

import styles from './BankAccountsPage.module.scss'

const useBankAccounts = () => {
  const apiClient = useApiClient(ClientApiClient)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.profile.bankAccounts(),
    queryFn: () => apiClient.getBankAccounts(),
  })

  return { data, isLoading }
}

export const BankAccountsPage: React.FC = () => {
  useScrollToTop()
  const isMobile = useWindowResize()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { scrollIntoView } = useScrollToElementIds()
  scrollIntoView([ScrollToIds.ProfileBankAccountsHeader])
  const { data, isLoading } = useBankAccounts()

  const { checkAccountTradersRoomAccess } = useAccountActionAccessCheck()

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      <NotificationWarning
        wrapperClassName={styles.notificationWrapper}
        title={t('Notifications.Note')}
        subtitle={
          <p className={styles.notificationSubtitle}>
            {t(
              'Profile.Only upload your bank details for International Bank Deposits and Withdrawals'
            )}
          </p>
        }
      />
      <PageSection
        id={ScrollToIds.ProfileBankAccountsHeader}
        title={t('Profile.Bank Accounts')}
        renderActions={[
          {
            onClick: () =>
              checkAccountTradersRoomAccess(() => navigate('/profile/bank-accounts/new')),
            title: `${t('Add New')}`,
            icon: CirclePlusIcon,
          },
        ]}
      />
      {isMobile ? (
        <BankAccountsCard bankAccounts={data} />
      ) : (
        <BankAccountsTable bankAccounts={data} />
      )}
    </Loading>
  )
}
